import React, { useState, useEffect } from "react";
import {
  FiFilter,
  FiCheck,
  FiX,
  FiClock,
  FiUser,
  FiPhone,
  FiMail,
  FiDollarSign,
  FiCalendar,
  FiImage,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiUserCheck,
} from "react-icons/fi";
import { getApprovedDeposits } from "../../api/endpoints";
import Swal from "../../utils/swalCompat";

import PageHeader from "../../components/PageHeader";

const DepositApproved = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [baseUrl, setBaseUrl] = useState(""); // ✅ Add baseUrl state

  // ✅ Fetch Approved Deposits
  const fetchApprovedDeposits = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search.trim(),
        startDate,
        endDate,
      };

      const res = await getApprovedDeposits(params);
      console.log("Approved Deposits:", res);

      // ✅ Save baseUrl from response
      if (res.data?.baseUrl) {
        setBaseUrl(res.data.baseUrl);
        console.log("Base URL set:", res.data.baseUrl);
      }

      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching approved deposits:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch approved deposits",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedDeposits();
  }, [page, search, startDate, endDate]);

  // ✅ Get Full Image URL (same as DepositPending)
  const getFullImageUrl = (screenshot) => {
    if (!screenshot) return "";
    return screenshot.startsWith("http")
      ? screenshot
      : `${baseUrl}/${screenshot}`;
  };

  // ✅ Format Date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Get User Name
  const getUserName = (item) => {
    if (item.userName && item.userName.trim() !== "" && item.userName !== "N/A") {
      return item.userName;
    }
    return "N/A";
  };

  // ✅ Get Status Badge
  const getStatusBadge = (status) => {
    const statusMap = {
      APPROVED: { color: "badge-light-success", icon: <FiCheck />, label: "Approved" },
      PENDING: { color: "badge-light-warning", icon: <FiClock />, label: "Pending" },
      REJECTED: { color: "badge-light-danger", icon: <FiX />, label: "Rejected" },
    };
    const statusInfo = statusMap[status] || statusMap.APPROVED;
    return (
      <span className={`${statusInfo.color}`}>
        {statusInfo.icon} {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="lead-page">
      <div className="deposit-approved-page">
        {/* Header */}
        <PageHeader
          eyebrow="Manual Payment"
          title="Deposit Approved"
          subtitle="View all approved deposit requests with transaction details."
          icon={<FiCheckCircle />}
          tone="success"
          stat={{ label: "Total Approved", value: total }}
        />

        {/* Card */}
        <div className="card shadow-sm border-0">
          {/* Card Header */}
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap border-bottom">
            <h5 className="mb-0 fw-bold">
              <FiCheckCircle className="me-2 text-success" />
              Approved Deposit Requests
            </h5>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {showFilter && (
                <>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by User, UTR..."
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    style={{ width: "200px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => {
                      setPage(1);
                      setStartDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => {
                      setPage(1);
                      setEndDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                </>
              )}
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FiFilter /> Filter
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchApprovedDeposits}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FiRefreshCw /> Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th style={{ minWidth: "200px" }}>User Details</th>
                    <th style={{ width: "120px" }}>Amount</th>
                    <th style={{ minWidth: "130px" }}>UTR Number</th>
                    <th style={{ width: "120px" }}>Payment</th>
                    <th style={{ width: "80px" }}>Screenshot</th>
                    <th style={{ minWidth: "150px" }}>Approved Date</th>
                    <th style={{ width: "110px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 text-muted">Loading approved deposits...</div>
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => {
                      const imageUrl = getFullImageUrl(item.screenshot);
                      console.log("Image URL:", imageUrl); // Debugging
                      return (
                        <tr key={item._id}>
                          <td className="fw-bold">{(page - 1) * limit + index + 1}</td>

                          {/* User Details */}
                          <td>
                            <div className="user-info-box">
                              <div className="user-name">
                                <div className="user-icon">
                                  <FiUser size={14} />
                                </div>
                                <strong>{getUserName(item)}</strong>
                              </div>
                              <div className="user-detail">
                                <FiPhone />
                                <span>{item.userMobile || "N/A"}</span>
                              </div>
                              {/* <div className="user-detail">
                                <FiMail />
                                <span>{item.userEmail || "N/A"}</span>
                              </div>
                              <div className="user-referral">
                                Referral: {item.userReferral || "-"}
                              </div> */}
                            </div>
                          </td>

                          {/* Amount */}
                          <td>
                            <span className="fw-medium text-success fs-6">
                              ₹ {item.amount?.toLocaleString() || 0}
                            </span>
                          </td>

                          {/* UTR Number */}
                          <td>
                            <span className="badge_utr">
                              {item.utrNumber || "N/A"}
                            </span>
                          </td>

                          {/* Payment Method */}
                          <td>
                            <span className="badge bg-secondary bg-opacity-10 text-dark">
                              {item.paymentMethod || "N/A"}
                            </span>
                          </td>

                          {/* Screenshot - ✅ Same as DepositPending */}
                          <td>
                            {imageUrl ? (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  console.log("Opening image:", imageUrl);
                                  setSelectedImage(imageUrl);
                                }}
                              >
                                <FiEye /> View
                              </button>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>

                          {/* Approved Date */}
                          <td>
                            <div className="small">
                              <FiCalendar size={10} className="me-1" />
                              {item.approvedAt ? formatDate(item.approvedAt) : "N/A"}
                            </div>
                          </td>

                          {/* Status */}
                          <td>{getStatusBadge(item.status)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <div className="text-muted">
                          <FiCheckCircle size={40} className="mb-3 d-block mx-auto text-secondary" />
                          <h5 className="fw-bold">No Approved Deposits</h5>
                          <p className="small">No deposits have been approved yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div>
                  <span className="text-muted small">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} entries
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <span className="fw-bold px-3">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Screenshot Modal - Same as DepositPending */}
        {selectedImage && (
          <div
            className="modal show d-block"
            style={{
              backgroundColor: "rgba(0,0,0,0.7)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onClick={() => setSelectedImage(null)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "600px", width: "100%", margin: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deposit Screenshot</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedImage(null)}
                  />
                </div>
                <div className="modal-body text-center">
                  <img
                    src={selectedImage}
                    alt="Deposit Screenshot"
                    crossOrigin="anonymous"
                    className="img-fluid rounded"
                    style={{ maxHeight: "400px", objectFit: "contain" }}
                    onLoad={() => console.log("Image Loaded Successfully")}
                    onError={(e) => {
                      console.error("Image failed to load:", e.target.src);
                      e.target.style.display = 'none';
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "Failed to load image. Please check if the URL is correct.",
                      });
                    }}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedImage(null)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.open(selectedImage, "_blank")}
                  >
                    Open Full Size
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositApproved;
